import * as React from 'react';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';

import { Typography2 } from '@coursera/cds-core';
import { css } from '@coursera/coursera-ui';

import type { InformationCardProps } from 'bundles/program-common/components/InformationCard/InformationCard';
import TabListInformationCard from 'bundles/program-common/components/InformationCard/TabListInformationCard';
import TabWithCaption from 'bundles/program-common/components/InformationCard/TabWithCaption';
import { skillTargetsStyles } from 'bundles/program-common/components/StaticInformationCards/styles';
import SvgProficiencyLevels from 'bundles/program-common/svg/SvgProficiencyLevels';
import SvgScoreAfterPassingAssessments from 'bundles/program-common/svg/SvgScoreAfterPassingAssessments';
import SvgStartingScore from 'bundles/program-common/svg/SvgStartingScore';
import SvgTargetProficiencyScore from 'bundles/program-common/svg/SvgTargetProficiencyScore';

import _t from 'i18n!nls/program-common';

type SkillTargetsInformationCardProps = Omit<InformationCardProps, 'trackingName' | 'trackingData'> & {
  hasProficiencyTargetsTab?: boolean;
  hideTarget?: boolean;
};

const SkillTargetsInformationCard: React.FC<SkillTargetsInformationCardProps> = (props) => {
  const { hasProficiencyTargetsTab = true } = props;
  const whatAreProficiencyTargetsComponent = (
    <TabWithCaption label={_t('What are proficiency targets')}>
      <div>
        <Typography2 component="p">
          {_t(
            'Proficiency targets are indicators of skill proficiency defined by your organization to communicate skill development goals and expectations.'
          )}
        </Typography2>
        <Typography2 component="p">
          {_t(
            'They are originally informed by Coursera platform data and subject matter experts, and customized by your organization.'
          )}
        </Typography2>
      </div>
      <div {...css(skillTargetsStyles.demoBlock)}>
        <SvgTargetProficiencyScore hideTarget={props.hideTarget} />
      </div>
    </TabWithCaption>
  );
  const howToDevelopSkillScoresComponent = (
    <TabWithCaption label={_t('How to develop skill scores')}>
      <div>
        <Typography2 component="p">
          {_t('Your initial score is 0 but it increases when you pass course assessments.')}
        </Typography2>
        <Typography2 component="p">
          {_t(
            'Enroll in one or more recommended courses. If you pass harder assessments, your score will increase faster. The higher you go, the more advanced content you need to take to increase your score.'
          )}
        </Typography2>
      </div>
      <div {...css(skillTargetsStyles.demoBlock)}>
        <Typography2 component="p">{_t('Starting score')}</Typography2>
        <SvgStartingScore hideTarget={props.hideTarget} />
        <Typography2 component="p">{_t('Score after passing assessments')}</Typography2>
        <SvgScoreAfterPassingAssessments hideTarget={props.hideTarget} />
      </div>
    </TabWithCaption>
  );
  const howSkillScoresAreCalulcatedComponent = (
    <TabWithCaption label={_t('How skill scores are calculated')}>
      <div>
        <Typography2 component="p">
          {_t(
            'Your skill score is calculated based on your performance on graded assessments. As you pass assessments, your skill score generally increases, but how much depends on the difficulty of the material.'
          )}
        </Typography2>
        <Typography2 component="p">
          {_t(
            'Your performance on assessments is weighted by difficulty, similar to a rating in competitive sports. If you pass harder assessments, your score will increase faster.'
          )}
        </Typography2>
      </div>
      <div>
        <Typography2 component="p">
          <FormattedHTMLMessage
            message={_t(
              'If you pass easier assessments, your score will increase slower. The higher you go, the more advanced content you need to take to increase your score. If you want to know what is under the hood of the data model that powers skill scoring, <a target="_blank" rel="nofollow noopener" href="https://files.eric.ed.gov/fulltext/ED599237.pdf">read this research paper</a> that explains all the nuts and bolts.'
            )}
          />
        </Typography2>
      </div>
    </TabWithCaption>
  );

  const whoCanViewMySkillScoreComponent = (
    <TabWithCaption label={_t('Who can view my skill scores')}>
      <div>
        <Typography2 component="p">
          {_t(
            'If you use Coursera through your organization, your program administrator will be able to see your skill scores and the associated proficiency level (conversant, beginner, intermediate, advanced).'
          )}
        </Typography2>
      </div>
    </TabWithCaption>
  );

  const whatProficiencyLevelsComponent = (
    <TabWithCaption label={_t('What are proficiency levels')}>
      <div>
        <Typography2 component="p">
          {_t(
            'Your skill scores are translated into skill proficiency levels. Your program administrator can see your proficiency level for each skill you are learning and the average proficiency level of all learners in the organization learning a skill.'
          )}
        </Typography2>
      </div>
      <div>
        <SvgProficiencyLevels />

        <dl {...css(skillTargetsStyles.definitionList)}>
          <dt>{_t('1-50')}</dt>
          <dd>{_t('Conversant level')}</dd>

          <dt>{_t('51-150')}</dt>
          <dd>{_t('Beginner level')}</dd>

          <dt>{_t('151-350')}</dt>
          <dd>{_t('Intermediate level')}</dd>

          <dt>{_t('351-500')}</dt>
          <dd>{_t('Advanced level')}</dd>
        </dl>
      </div>
    </TabWithCaption>
  );

  return (
    <TabListInformationCard id="proficiency-targets-faq" {...props} trackingName="skill_targets_information_card">
      {[
        ...(hasProficiencyTargetsTab ? [whatAreProficiencyTargetsComponent] : []),
        howToDevelopSkillScoresComponent,
        howSkillScoresAreCalulcatedComponent,
        whoCanViewMySkillScoreComponent,
        whatProficiencyLevelsComponent,
      ]}
    </TabListInformationCard>
  );
};

export default SkillTargetsInformationCard;
